import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { formatHourLabel } from "@/lib/v2/pollAggregates";
import { HorizontalScrollStrip } from "@/components/v2/home/HorizontalScrollStrip";

export type PollHourStripVariant = "vote" | "admin";

interface PollHourStripProps {
  hours: number[];
  selectedHour: number | null;
  onSelectHour: (hour: number) => void;
  /** vote: green selection; admin: amber selection */
  variant?: PollHourStripVariant;
  /** slotId -> vote count (admin variant shows counts) */
  voteCounts?: Map<number, number>;
  /** hours that are the top pick */
  topPickHours?: Set<number>;
  /** For vote variant: which hours are selected (multi-select via slot ids handled by parent) */
  isHourSelected?: (hour: number) => boolean;
  isHourSuggested?: (hour: number) => boolean;
  /** Toggle vs single-select — vote uses toggle via parent */
  onToggleHour?: (hour: number) => void;
}

const CHIP_MIN_W = "min-w-[3.5rem]"; // 56px

export function PollHourStrip({
  hours,
  selectedHour,
  onSelectHour,
  variant = "vote",
  voteCounts,
  topPickHours,
  isHourSelected,
  isHourSuggested,
  onToggleHour,
}: PollHourStripProps) {
  const { t } = useTranslation();

  const handleClick = (hour: number) => {
    if (onToggleHour) onToggleHour(hour);
    else onSelectHour(hour);
  };

  if (hours.length === 0) return null;

  const isMultiSelect = variant === "vote" && Boolean(onToggleHour);

  return (
    <HorizontalScrollStrip
      hint={
        <p className="text-[11px] text-muted-foreground md:hidden">
          {t("v2.home.swipeHoursHint")}
        </p>
      }
      role="listbox"
      aria-label={t("v2.home.hoursLabel")}
      aria-multiselectable={isMultiSelect || undefined}
    >
      {hours.map((hour) => {
        const isAdmin = variant === "admin";
        const selected = isAdmin
          ? selectedHour === hour
          : (isHourSelected?.(hour) ?? selectedHour === hour);
        const suggested = !isAdmin && (isHourSuggested?.(hour) ?? false);
        const voteCount = voteCounts?.get(hour) ?? 0;
        const isTopPick = isAdmin && (topPickHours?.has(hour) ?? false);

        return (
          <button
            key={hour}
            type="button"
            role="option"
            aria-selected={selected}
            onClick={() => handleClick(hour)}
            className={cn(
              "flex shrink-0 snap-start flex-col items-center justify-center rounded-xl border-2 px-3 py-2.5 transition-transform active:scale-[0.97]",
              CHIP_MIN_W,
              "min-h-[44px]",
              isAdmin
                ? selected
                  ? "border-amber-500/70 bg-amber-500/15 ring-1 ring-amber-500/30"
                  : isTopPick
                    ? "border-amber-400/45 bg-amber-400/8"
                    : "border-border bg-card"
                : selected
                  ? "border-soccer-primary bg-soccer-primary/15"
                  : suggested
                    ? "border-soccer-primary/35 bg-card ring-1 ring-soccer-primary/20"
                    : "border-border bg-card"
            )}
          >
            <span
              className={cn(
                "text-sm font-bold tabular-nums leading-none",
                isAdmin
                  ? selected
                    ? "text-amber-900 dark:text-amber-100"
                    : "text-foreground"
                  : selected
                    ? "text-soccer-primary"
                    : "text-foreground"
              )}
            >
              {formatHourLabel(hour)}
            </span>
            {isAdmin && voteCount > 0 && (
              <span className="mt-1 text-[10px] font-semibold tabular-nums text-muted-foreground">
                {t("v2.home.adminSlotVotes", { count: voteCount })}
              </span>
            )}
          </button>
        );
      })}
    </HorizontalScrollStrip>
  );
}
